import { isMobile } from 'react-device-detect'
import React, { FC, useCallback, useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/router'
import { BottomSheetWithItemIcon, styled } from '~/app/components'
import useModalHash from '~/app/components/common/useModalHash'
import useShare from '~/app/components/order-list/hooks/useShare'
import { useLoading } from '~/app/components/common/LoadingProvider'
import { useRequestOrderShareMutation } from '~/__generated__/app/query/order-shared/request-order-share.graphql'
import conf from '~/app/conf'
import UAParser from 'ua-parser-js'
import { sendKinesisShareClick } from '~/app/kinesis/order-detail-page'
import { getYaNative } from '@yanolja-rnd/ya-native'
import { Popup } from '@yanolja-rnd/ya-com-popup'
import { notEmpty } from '~/app/utils'
import { isNative } from '~/app/utils/typeguards'
import { Toast, ToastHandler } from '@yanolja-rnd/ya-com-toast'
import { getIsNotAuthorized, goToDomesticAuthPage } from '~/app/utils/useErrorHandler'
import { Icon_share } from '@yanolja-rnd/ya-com-system-icon'

/**
 * ShareButton component
 * author: soojin.oh
 */

const ShareIconWrap = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
`
interface MyDocument extends Document {
  webkitHidden: boolean
}

interface ShareMethod {
  facebook
  sms
  kakao
  link
}

// ShareButton component
const ShareButton: FC<{
  orderId: string
  hashOrderId: string
  isOrderComplete?: boolean
}> = function ({ orderId, hashOrderId, isOrderComplete }) {
  const toastRef = useRef<ToastHandler>()
  const triggerToast = useCallback(message => {
    toastRef.current?.addMessage({ message, timeout: 3000 })
  }, [])
  const router = useRouter()
  const [shareUrl, setShareUrl] = useState('')
  const [isShareOpen, setShareOpen] = useModalHash({ id: 'share' })
  const [isDialogOpen, setDialogOpen] = useModalHash({ id: 'session' })
  const [isKakaoDialogOpen, setKakaoDialogOpen] = useModalHash({ id: 'kakao' })
  const [items, setItems] = useState<{ title: string; id: string; icon: any }[]>([])
  const { kakaoShare, facebookShare, smsShare, linkShare } = useShare()

  const onError = useCallback(error => {
    const isNotAuthorized = getIsNotAuthorized(error)
    setShareOpen(false)
    if (isNotAuthorized) {
      setTimeout(() => setDialogOpen(true), 100) // bottom sheet 를 닫으면서 다이얼로그를 동시에 열면 정상적으로 동작이 안됨
    }
  }, [])

  const [requestOrderShareMutation, { loading, error }] = useRequestOrderShareMutation({ onError })
  useLoading(loading)

  const onDialogClose = useCallback(() => {
    const yaNative = getYaNative()
    setTimeout(() => {
      if (isNative(yaNative)) {
        const deeplink = 'yanoljamotel://etc' // 마이 야놀자 딥링크
        yaNative.deepLink({ link: deeplink })
      } else {
        router.push(`${conf.web.url}/mypage`)
      }
    }, 100) // 다이얼로그가 닫히면서 주소를 변경하는 작업을하면 정상적으로 동작이 안됨
  }, [])

  const onKakaoDialogClick = useCallback(
    id => {
      setKakaoDialogOpen(false)
      if (id === 'install') {
        setTimeout(() => {
          const os = new UAParser().getOS().name
          if (os === 'iOS') {
            location.href =
              'https://apps.apple.com/kr/app/%EC%B9%B4%EC%B9%B4%EC%98%A4%ED%86%A1-kakaotalk/id362057947'
          } else if (os === 'Android') {
            location.href = 'https://play.google.com/store/apps/details?id=com.kakao.talk'
          }
        }, 100)
      }
    },
    [shareUrl]
  )

  const checkKakaoInstalled = useCallback(() => {
    const uaParser = new UAParser()
    const isKakaoEmbedded = uaParser
      .getUA()
      .split(' ')
      .some(agent => agent.endsWith('KAKAOTALK'))
    const isMobile = uaParser.getDevice().type === 'mobile'
    // const isTablet = new UAParser().getDevice().type === 'table'
    const yaNative = getYaNative()
    if (isMobile && !isNative(yaNative) && !isKakaoEmbedded) {
      /**
       * 모바일인 경우에만 카카오톡의 경우 설치 여부를 판단해야한다.
       * 웹뷰에서는 카카오 sdk 에서 팝업형태로 share 웹 화면을 제공하므로 카카오 설치 팝업을 띄우지 않는다.
       */
      setTimeout(() => {
        const docu = document as MyDocument
        if (!docu.webkitHidden! && !document.hidden) {
          // 앱이 없는 경우
          setKakaoDialogOpen(true)
        } else {
          setKakaoDialogOpen(false)
        }
      }, 1000)
    }
  }, [])

  const onItemSelected = useCallback(
    async id => {
      sendKinesisShareClick(id, hashOrderId, isOrderComplete)
      let url = shareUrl
      if (!url) {
        const { data } = (await requestOrderShareMutation({ variables: { orderId } })) || {}
        if (!data) {
          return
        }
        url = `${location.origin}/share/${data?.requestOrderShare.key}`
        setShareUrl(url)
      }

      if (id === 'kakao') {
        kakaoShare(url, false)
        checkKakaoInstalled()
      } else if (id === 'facebook') {
        facebookShare(url)
      } else if (id === 'sms') {
        smsShare(url)
      } else if (id === 'link') {
        linkShare(url)
        triggerToast('주소를 복사했어요')
      }
      setTimeout(() => setShareOpen(false), 100) // 주소를 변경하는 작업 두개가 겹치면 정상적으로 동작이 안됨
    },
    [kakaoShare, facebookShare, smsShare, linkShare, orderId, hashOrderId, shareUrl]
  )

  useEffect(() => {
    // 스크립트 로드가 비동기로 일어나기 때문에 페이지가 열리는 순간에는 Kakao namespace 가 생성되지 않은 상태이다
    // isShareOpen 을 deps 에 추가하여 Kakao namespace 생성여부를 버튼이 열릴때마다 확인한다
    const links: ({ title: string; id: keyof ShareMethod; icon: any } | null)[] = [
      typeof Kakao !== 'undefined'
        ? {
            title: '카카오톡',
            id: 'kakao',
            icon: require('~/app/icons/bottomsheet-foundation-icon-kakaotalk.svg?inline'),
          }
        : null,
      {
        title: '페이스북',
        id: 'facebook',
        icon: require('~/app/icons/bottomsheet-foundation-icon-facebook.svg?inline'),
      },
      {
        title: 'SMS',
        id: 'sms',
        icon: require('~/app/icons/bottomsheet-foundation-icon-sms.svg?inline'),
      },
      {
        title: '링크복사',
        id: 'link',
        icon: require('~/app/icons/bottomsheet-foundation-icon-copylink.svg?inline'),
      },
    ]
    const filteredLinks = links
      .filter(notEmpty)
      .filter(link => (isMobile ? true : link.id !== 'sms')) // mobile 이 아니면 sms 는 제외
    setItems(filteredLinks)
  }, [isMobile, isShareOpen])

  const onDialogButtonClick = useCallback(_ => goToDomesticAuthPage(error), [])

  return (
    <>
      <ShareIconWrap onClick={() => setShareOpen(true)}>
        <Icon_share size={2.4} unit="rem" />
      </ShareIconWrap>
      <BottomSheetWithItemIcon
        title={'공유하기'}
        isOpened={isShareOpen}
        onRequestClose={() => setShareOpen(false)}
        items={items}
        onItemSelected={onItemSelected}
        onBackgroundClick={() => setShareOpen(false)}
      />
      <Toast toastRef={toastRef} />
      <Popup
        popupId={'SHARE_SESSION_EXPIRED'}
        title={'세션이 만료되었습니다.'}
        description={'계속하려면 로그인 또는 비회원 예약 조회 후 다시 시도해주세요.'}
        isOpened={isDialogOpen}
        buttons={[
          {
            id: 'confirm',
            title: '확인',
            isPrimary: true,
          },
        ]}
        onButtonClick={onDialogButtonClick}
        onPopupClose={onDialogClose}
      />
      <Popup
        popupId={'SHARE_KAKAO_INSTALL'}
        title={'카카오톡을 설치해주세요'}
        isOpened={isKakaoDialogOpen}
        buttons={[
          {
            id: 'close',
            title: '닫기',
          },
          {
            id: 'install',
            title: '설치하러가기',
            isPrimary: true,
          },
        ]}
        onButtonClick={onKakaoDialogClick}
      />
    </>
  )
}

export default ShareButton
